import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/context/AuthContext";
import { Shield, Eye, EyeOff, AlertCircle, CheckCircle, Smartphone, Copy } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function LoginPage() {
  const [tab, setTab] = useState("login");
  const [step, setStep] = useState("login"); // login | mfa_verify | mfa_setup
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // MFA state
  const [tempToken, setTempToken] = useState("");
  const [mfaCode, setMfaCode] = useState("");
  const [mfaSetupQr, setMfaSetupQr] = useState("");
  const [mfaSetupSecret, setMfaSetupSecret] = useState("");
  const [secretCopied, setSecretCopied] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/login", { email, password });
      if (res.data.mfa_required) {
        setTempToken(res.data.temp_token);
        setStep("mfa_verify");
      } else if (res.data.mfa_setup_required) {
        setTempToken(res.data.temp_token);
        // Initiate MFA setup
        const setupRes = await api.post("/auth/mfa/setup", { temp_token: res.data.temp_token });
        setMfaSetupQr(setupRes.data.qr_image_base64 || "");
        setMfaSetupSecret(setupRes.data.secret || "");
        setStep("mfa_setup");
      } else {
        login(res.data.token, res.data.user);
        const role = res.data.user?.role;
        navigate(role === "platform_admin" ? "/platform" : ["lc_manager","lc_staff"].includes(role) ? "/lc-dashboard" : "/dashboard");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa/complete-login", { temp_token: tempToken, code: mfaCode });
      login(res.data.token, res.data.user);
      const role = res.data.user?.role;
      navigate(role === "platform_admin" ? "/platform" : ["lc_manager","lc_staff"].includes(role) ? "/lc-dashboard" : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSetupVerify = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await api.post("/auth/mfa/verify-setup", { temp_token: tempToken, code: mfaCode });
      login(res.data.token, res.data.user);
      const role = res.data.user?.role;
      navigate(role === "platform_admin" ? "/platform" : ["lc_manager","lc_staff"].includes(role) ? "/lc-dashboard" : "/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard.writeText(mfaSetupSecret).then(() => {
      setSecretCopied(true);
      setTimeout(() => setSecretCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen flex" style={{ background: "linear-gradient(135deg, #1B2B5E 0%, #0F172A 100%)" }}>
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12">
        <Link to="/" className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/15 rounded-xl flex items-center justify-center">
            <Shield className="w-6 h-6 text-white" />
          </div>
          <span className="font-heading font-bold text-white text-xl">RiskSurvey<span className="text-white/50">.ai</span></span>
        </Link>

        <div className="space-y-8">
          <div>
            <h1 className="font-heading text-4xl font-bold text-white leading-tight mb-4">
              AI-Powered Loss Control Inspections
            </h1>
            <p className="text-white/60 text-lg leading-relaxed">
              Conduct comprehensive insurance inspections through an intelligent chat interface. Get instant photo analysis, structured findings, and professional PDF reports.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {[
              { label: "Inspection Types", value: "5" },
              { label: "AI Confidence Scoring", value: "100%" },
              { label: "Report Generation", value: "Auto" },
              { label: "Photo Analysis", value: "Real-time" },
            ].map((s) => (
              <div key={s.label} className="bg-white/10 rounded-xl p-4">
                <div className="text-white font-heading font-bold text-2xl">{s.value}</div>
                <div className="text-white/50 text-sm mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/30 text-sm">© 2026 RiskSurvey.ai — All rights reserved</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl p-8 animate-slide-up">
          <div className="lg:hidden flex items-center gap-2 mb-6">
            <Shield className="w-6 h-6 text-navy" />
            <span className="font-heading font-bold text-navy text-lg">RiskSurvey.ai</span>
          </div>

          {/* MFA Verify Screen */}
          {step === "mfa_verify" && (
            <div data-testid="mfa-verify-screen">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-navy text-lg">Two-Factor Authentication</h2>
                  <p className="text-slate-500 text-sm">Enter the code from your authenticator app</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              <form onSubmit={handleMfaVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Verification Code</label>
                  <input
                    data-testid="mfa-code-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    autoFocus
                    className="w-full h-12 px-4 text-center text-2xl tracking-widest border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors"
                  />
                </div>
                <button
                  data-testid="mfa-verify-btn"
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  className="w-full h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors duration-150 disabled:opacity-60"
                >
                  {loading ? "Verifying..." : "Verify Code"}
                </button>
                <button
                  type="button"
                  onClick={() => { setStep("login"); setError(""); setMfaCode(""); }}
                  className="w-full text-sm text-slate-400 hover:text-navy transition-colors"
                >
                  Back to login
                </button>
              </form>
            </div>
          )}

          {/* MFA Setup Screen */}
          {step === "mfa_setup" && (
            <div data-testid="mfa-setup-screen">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-navy/10 rounded-full flex items-center justify-center">
                  <Shield className="w-5 h-5 text-navy" />
                </div>
                <div>
                  <h2 className="font-heading font-bold text-navy text-lg">Set Up Two-Factor Authentication</h2>
                  <p className="text-slate-500 text-sm">Scan the QR code with your authenticator app</p>
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}

              {mfaSetupQr && (
                <div className="mb-4 flex justify-center">
                  <img src={`data:image/png;base64,${mfaSetupQr}`} alt="MFA QR Code" className="w-48 h-48 border rounded-lg" />
                </div>
              )}

              {mfaSetupSecret && (
                <div className="mb-4 p-3 bg-slate-50 rounded-lg flex items-center justify-between gap-2">
                  <span className="text-xs font-mono text-slate-600 break-all">{mfaSetupSecret}</span>
                  <button
                    type="button"
                    onClick={copySecret}
                    className="flex-shrink-0 p-1.5 hover:bg-slate-200 rounded"
                    data-testid="copy-secret-btn"
                  >
                    {secretCopied ? <CheckCircle className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-slate-400" />}
                  </button>
                </div>
              )}

              <form onSubmit={handleMfaSetupVerify} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Enter verification code to confirm</label>
                  <input
                    data-testid="mfa-setup-code-input"
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={mfaCode}
                    onChange={e => setMfaCode(e.target.value.replace(/\D/g, ""))}
                    required
                    placeholder="000000"
                    className="w-full h-12 px-4 text-center text-2xl tracking-widest border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors"
                  />
                </div>
                <button
                  data-testid="mfa-setup-verify-btn"
                  type="submit"
                  disabled={loading || mfaCode.length !== 6}
                  className="w-full h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors duration-150 disabled:opacity-60"
                >
                  {loading ? "Activating..." : "Activate MFA"}
                </button>
              </form>
            </div>
          )}

          {/* Login only — registration is done by platform admin */}
          {step === "login" && (
            <>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm" data-testid="auth-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              {success && (
                <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="auth-success">
                  {success}
                </div>
              )}

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Email Address</label>
                  <input
                    data-testid="email-input"
                    type="email" value={email} onChange={e => setEmail(e.target.value)}
                    required placeholder="you@company.com"
                    className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Password</label>
                  <div className="relative">
                    <input
                      data-testid="password-input"
                      type={showPassword ? "text" : "password"} value={password} onChange={e => setPassword(e.target.value)}
                      required placeholder="••••••••"
                      className="w-full h-10 px-3 pr-10 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy transition-colors"
                    />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Link to="/forgot-password" className="text-xs text-navy hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
                </div>
                <button
                  data-testid="login-submit-btn"
                  type="submit" disabled={loading}
                  className="w-full h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors duration-150 disabled:opacity-60"
                >{loading ? "Signing in..." : "Sign In"}</button>
                <button type="button" data-testid="use-code-login-btn"
                  onClick={() => { setStep("code"); setError(""); setSuccess(""); }}
                  className="w-full text-xs text-slate-400 hover:text-navy transition-colors pt-1">
                  Or email me a one-time login code
                </button>
              </form>
            </>
          )}

          {/* Email code login */}
          {step === "code" && (
            <div data-testid="code-login-screen">
              <h2 className="font-heading text-xl font-bold text-slate-800 mb-1">Login with a code</h2>
              <p className="text-slate-500 text-sm mb-5">We'll email a 6-digit code to your account address.</p>
              {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm" data-testid="auth-error">{error}</div>}
              {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm" data-testid="code-sent-msg">{success}</div>}
              <div className="space-y-4">
                <input data-testid="code-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
                {!success ? (
                  <button data-testid="send-code-btn" disabled={loading || !email}
                    onClick={async () => {
                      setLoading(true); setError("");
                      try {
                        await api.post("/auth/request-code", { email });
                        setSuccess("Code sent — check your inbox (expires in 10 minutes)");
                      } catch (err) { setError(err.response?.data?.detail || "Could not send code"); }
                      setLoading(false);
                    }}
                    className="w-full h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
                    {loading ? "Sending..." : "Send Code"}
                  </button>
                ) : (
                  <>
                    <input data-testid="code-input" value={mfaCode} onChange={e => setMfaCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="6-digit code" inputMode="numeric"
                      className="w-full h-10 px-3 border border-slate-200 rounded-lg text-sm text-center tracking-[0.4em] font-mono focus:outline-none focus:ring-2 focus:ring-navy/30 focus:border-navy" />
                    <button data-testid="verify-code-btn" disabled={loading || mfaCode.length !== 6}
                      onClick={async () => {
                        setLoading(true); setError("");
                        try {
                          const res = await api.post("/auth/verify-code", { email, code: mfaCode });
                          login(res.data.token, res.data.user);
                          const role = res.data.user?.role;
                          navigate(role === "platform_admin" ? "/platform" : role === "broker" ? "/broker" : ["lc_manager", "lc_staff"].includes(role) ? "/lc-dashboard" : "/dashboard");
                        } catch (err) { setError(err.response?.data?.detail || "Verification failed"); }
                        setLoading(false);
                      }}
                      className="w-full h-10 bg-navy text-white rounded-lg text-sm font-medium hover:bg-navy-dark transition-colors disabled:opacity-60">
                      {loading ? "Verifying..." : "Verify & Sign In"}
                    </button>
                  </>
                )}
                <button type="button" data-testid="back-to-password-btn"
                  onClick={() => { setStep("login"); setError(""); setSuccess(""); setMfaCode(""); }}
                  className="w-full text-xs text-slate-400 hover:text-navy transition-colors">
                  Back to password sign-in
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
